import { getLanguageOnlyFromLocale } from "@msys/tolgee";
import { languages } from "../../../common/translations/languages.js";
import { toRealmLocale } from "./signupAuthOptions.js";

/**
 * Language handover of the signup: the Keycloak pages let the visitor pick a
 * language, which ends up as the `locale` claim of the ID token (realm locales
 * `de`, `en`, `fr`, `pl`, `nl`, possibly with a region). The app maps it to the
 * language it supports; anything else stays the app's own decision.
 */

/**
 * Maps a realm locale (e.g. `en`, `de_AT`) to the app locale (e.g. `en-GB`,
 * `de`), or `null` when it is missing, not a string or not supported.
 */
export function mapRealmLocaleToAppLocale(realmLocale: unknown): string | null {
  if (typeof realmLocale !== "string") {
    return null;
  }
  const language = getLanguageOnlyFromLocale(realmLocale).toLowerCase();
  return Object.prototype.hasOwnProperty.call(languages, language)
    ? (languages[language]?.locale ?? null)
    : null;
}

/**
 * The app locale the ID token's `locale` claim hands over, or `null` when the
 * app keeps its language. The claim only counts when it differs from the realm
 * locale the app itself requested (`ui_locales`): the realm has no Russian, so
 * for a Russian app the login pages come back in German and the claim merely
 * echoes that fallback.
 */
export function getHandedOverLocale(
  idTokenParsed: unknown,
  currentAppLanguage: string | undefined
): string | null {
  if (typeof idTokenParsed !== "object" || idTokenParsed === null) {
    return null;
  }
  const claim = "locale" in idTokenParsed ? idTokenParsed.locale : undefined;
  const appLocale = mapRealmLocaleToAppLocale(claim);
  if (appLocale === null || typeof claim !== "string") {
    return null;
  }
  if (
    currentAppLanguage !== undefined &&
    toRealmLocale(currentAppLanguage) ===
      getLanguageOnlyFromLocale(claim).toLowerCase()
  ) {
    return null;
  }
  return appLocale;
}
